import type React from 'react';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { styled } from '@mui/material/styles';

// Redux
import {
  setCsatDetailVisibility,
  setCsatFeedback,
  setCsatScore,
  setCsatVisibility,
} from 'actions/workflows/workflows';
import { submitCsatV2 } from 'actions/workflows-v2/workflowsV2';
import {
  selectCsatConfig,
  selectCsatFormData,
  selectTranslatedCsatText,
  selectWorkflowsConfig,
} from 'reducers/workflows';

// Helpers
import { CSAT_OPTIONS, handleImageType, getFeedbackOptions } from './utils';

import { selectLegibleElementColor } from 'reducers/config';
import { useTheme } from '@mui/material/styles';
import type { CsatFormData } from 'api/types';

interface CustomerSatisfactionOptionProps {
  label: (typeof CSAT_OPTIONS)[Exclude<CsatFormData['csatScore'], 0>];
  selectedScore: CsatFormData['csatScore'];
}

const CustomerSatisfactionOption: React.FC<
  React.PropsWithChildren<CustomerSatisfactionOptionProps>
> = ({ label, selectedScore }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const workflowsOrgConfig = useAppSelector(selectWorkflowsConfig);
  const { isCsatVisible } = useAppSelector(selectCsatConfig);
  const { csatScore } = useAppSelector(selectCsatFormData);
  const csatConfig = useAppSelector(selectCsatConfig);
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const isCsatSurveyFinished = Boolean(csatConfig?.isCsatSurveyFinished);
  const translatedCsatText = useAppSelector(selectTranslatedCsatText);

  const submitData = () => {
    if (workflowsOrgConfig?.csat_survey_config?.is_enabled) {
      dispatch(submitCsatV2());
      dispatch(setCsatDetailVisibility(true));
      dispatch(setCsatVisibility(true));
    }
  };

  const feedbackOptions = getFeedbackOptions(csatScore);

  const isCard = !isCsatVisible;

  const optionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isCsatVisible) {
      // setTimeout is needed because of the race condition with the native
      // autofocus that gets triggered when `showModal()` is called
      setTimeout(() => {
        optionRef.current?.focus();
      }, 0);
    }
  }, [isCsatVisible]);

  if (isCsatSurveyFinished && csatScore && isCard) {
    const labelKey = CSAT_OPTIONS[csatScore];
    const label = translatedCsatText?.text[labelKey] || '';
    const message =
      translatedCsatText?.text.CSAT_YOU_RATED?.replace('%s', label) || '';

    return (
      <EditContainer>
        <EditMessage color={theme.palette.colors.grey[600]}>
          {message}
        </EditMessage>
        <EditSurveyButton
          color={legibleElementColor}
          onClick={() => {
            dispatch(setCsatDetailVisibility(true));
            dispatch(setCsatVisibility(true));
          }}
        >
          {translatedCsatText?.text.CSAT_CHANGE_RATING}
        </EditSurveyButton>
      </EditContainer>
    );
  }

  const imageType = handleImageType(selectedScore, csatScore);
  const notSelected = csatScore < 1;
  const showNotSelectedLabel =
    (notSelected && selectedScore === 1) || selectedScore === 5;
  const showLabel = notSelected
    ? showNotSelectedLabel
    : csatScore === selectedScore;
  const shouldClear = feedbackOptions !== getFeedbackOptions(selectedScore);

  return (
    <Option
      aria-label={translatedCsatText?.text[label]}
      onClick={() => {
        dispatch(setCsatScore(selectedScore));
        shouldClear && dispatch(setCsatFeedback([]));
        dispatch(setCsatDetailVisibility(true));
        submitData();
      }}
      ref={csatScore === selectedScore ? optionRef : null}
      type='button'
    >
      <IconLabelContainer>
        <RateIcon
          alt=''
          data-testid={`rate-icon-${selectedScore}`}
          src={imageType}
        />
        <RateLabel
          className='rate-label'
          color={theme.palette.colors.grey[600]}
          data-testid={`rate-label-${selectedScore}`}
          showLabel={showLabel}
        >
          {translatedCsatText?.text[label]}
        </RateLabel>
      </IconLabelContainer>
    </Option>
  );
};

export default CustomerSatisfactionOption;

const EditContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const EditMessage = styled('p')<{
  color: string;
}>`
  color: ${props => props.color};
  text-align: center;
  margin: 0px;
`;

const EditSurveyButton = styled('button')<{
  color: string;
}>`
  cursor: pointer;
  color: ${props => props.color};
  border: none;
  background-color: transparent;
  font-weight: 500;
`;

const RateIcon = styled('img')`
  width: 40px;
  height: 40px;
  &:hover {
    width: 48px;
    height: 48px;
  }
  transition:
    all 150ms ease 0s,
    color 0ms ease 0s;
`;

const RateLabel = styled('div')<{
  color: string;
  showLabel: boolean;
}>`
  color: ${props => props.color};
  padding: 10px 0 0 0;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 14px;
  text-align: center;
  text-overflow: ellipsis;
  opacity: ${props => (props.showLabel ? 1 : 0)};
`;

const IconLabelContainer = styled('div')`
  &:hover .rate-label {
    opacity: 1;
  }
`;

const Option = styled('button')`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 50px;
  background: transparent;
  border: none;
  cursor: pointer;

  &:focus {
    outline: 2px solid var(--legible-element-color);
  }

  &:focus .rate-label {
    opacity: 1;
  }
`;
